/**
 * Where a player says they are playing from.
 *
 * Only the codes are shipped. Names come from `Intl.DisplayNames`, so they
 * arrive in the player's own language for free and no country name ever needs
 * a line in `en.json` — which is the only way a 250-entry list and the "all
 * copy goes through i18n" rule can both be true.
 *
 * ISO 3166-1 alpha-2, officially assigned, plus XK (Kosovo) — user-assigned,
 * but every list a player would recognise has it. Deprecated codes (SU, YU,
 * AN) and the reserved ones (EU, UK) are deliberately absent.
 */

export const COUNTRY_CODES: readonly string[] = [
  'AD',
  'AE',
  'AF',
  'AG',
  'AI',
  'AL',
  'AM',
  'AO',
  'AQ',
  'AR',
  'AS',
  'AT',
  'AU',
  'AW',
  'AX',
  'AZ',
  'BA',
  'BB',
  'BD',
  'BE',
  'BF',
  'BG',
  'BH',
  'BI',
  'BJ',
  'BL',
  'BM',
  'BN',
  'BO',
  'BQ',
  'BR',
  'BS',
  'BT',
  'BV',
  'BW',
  'BY',
  'BZ',
  'CA',
  'CC',
  'CD',
  'CF',
  'CG',
  'CH',
  'CI',
  'CK',
  'CL',
  'CM',
  'CN',
  'CO',
  'CR',
  'CU',
  'CV',
  'CW',
  'CX',
  'CY',
  'CZ',
  'DE',
  'DJ',
  'DK',
  'DM',
  'DO',
  'DZ',
  'EC',
  'EE',
  'EG',
  'EH',
  'ER',
  'ES',
  'ET',
  'FI',
  'FJ',
  'FK',
  'FM',
  'FO',
  'FR',
  'GA',
  'GB',
  'GD',
  'GE',
  'GF',
  'GG',
  'GH',
  'GI',
  'GL',
  'GM',
  'GN',
  'GP',
  'GQ',
  'GR',
  'GS',
  'GT',
  'GU',
  'GW',
  'GY',
  'HK',
  'HM',
  'HN',
  'HR',
  'HT',
  'HU',
  'ID',
  'IE',
  'IL',
  'IM',
  'IN',
  'IO',
  'IQ',
  'IR',
  'IS',
  'IT',
  'JE',
  'JM',
  'JO',
  'JP',
  'KE',
  'KG',
  'KH',
  'KI',
  'KM',
  'KN',
  'KP',
  'KR',
  'KW',
  'KY',
  'KZ',
  'LA',
  'LB',
  'LC',
  'LI',
  'LK',
  'LR',
  'LS',
  'LT',
  'LU',
  'LV',
  'LY',
  'MA',
  'MC',
  'MD',
  'ME',
  'MF',
  'MG',
  'MH',
  'MK',
  'ML',
  'MM',
  'MN',
  'MO',
  'MP',
  'MQ',
  'MR',
  'MS',
  'MT',
  'MU',
  'MV',
  'MW',
  'MX',
  'MY',
  'MZ',
  'NA',
  'NC',
  'NE',
  'NF',
  'NG',
  'NI',
  'NL',
  'NO',
  'NP',
  'NR',
  'NU',
  'NZ',
  'OM',
  'PA',
  'PE',
  'PF',
  'PG',
  'PH',
  'PK',
  'PL',
  'PM',
  'PN',
  'PR',
  'PS',
  'PT',
  'PW',
  'PY',
  'QA',
  'RE',
  'RO',
  'RS',
  'RU',
  'RW',
  'SA',
  'SB',
  'SC',
  'SD',
  'SE',
  'SG',
  'SH',
  'SI',
  'SJ',
  'SK',
  'SL',
  'SM',
  'SN',
  'SO',
  'SR',
  'SS',
  'ST',
  'SV',
  'SX',
  'SY',
  'SZ',
  'TC',
  'TD',
  'TF',
  'TG',
  'TH',
  'TJ',
  'TK',
  'TL',
  'TM',
  'TN',
  'TO',
  'TR',
  'TT',
  'TV',
  'TW',
  'TZ',
  'UA',
  'UG',
  'UM',
  'US',
  'UY',
  'UZ',
  'VA',
  'VC',
  'VE',
  'VG',
  'VI',
  'VN',
  'VU',
  'WF',
  'WS',
  'XK',
  'YE',
  'YT',
  'ZA',
  'ZM',
  'ZW',
];

const CODES = new Set(COUNTRY_CODES);

/** Guards anything arriving off the wire or out of an old install. */
export function isCountryCode(value: unknown): value is string {
  return typeof value === 'string' && CODES.has(value);
}

/**
 * The flag, built out of the code itself: 'CA' is the two regional-indicator
 * letters C and A, which every emoji font composes into a flag.
 *
 * Windows composes nothing — it has no flag glyphs — and shows the two letters
 * instead. That is a legible fallback rather than tofu, which is why the code
 * is the only thing this is ever built from.
 */
export function flagOf(code: string): string {
  if (!isCountryCode(code)) return '';
  const indicator = (index: number): number => 0x1f1e6 + code.charCodeAt(index) - 65;
  return String.fromCodePoint(indicator(0), indicator(1));
}

/** The country's name in `language`, falling back to the bare code. */
export function countryName(code: string, language: string): string {
  if (!isCountryCode(code)) return '';
  try {
    return new Intl.DisplayNames([language], { type: 'region' }).of(code) ?? code;
  } catch {
    // An engine without DisplayNames, or a language tag it will not parse.
    return code;
  }
}

/**
 * Every country, named and sorted for `language`. Sorting is by the localized
 * name under a locale-aware collation, so an accented name lands where a reader
 * of that language would look for it rather than after Z.
 */
export function countryOptions(
  language: string,
): readonly { readonly code: string; readonly name: string; readonly flag: string }[] {
  const collator = safeCollator(language);
  return COUNTRY_CODES.map((code) => ({
    code,
    name: countryName(code, language),
    flag: flagOf(code),
  })).sort((a, b) => collator(a.name, b.name));
}

function safeCollator(language: string): (a: string, b: string) => number {
  try {
    const collator = new Intl.Collator(language);
    return (a, b) => collator.compare(a, b);
  } catch {
    return (a, b) => (a < b ? -1 : a > b ? 1 : 0);
  }
}
